import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { I18nProvider } from "./i18n";
import Dashboard from "./pages/Dashboard";
import Projects from "./pages/Projects";
import Settings from "./pages/Settings";
import { useEffect } from "react";
import { trpc } from "@/lib/trpc";
import ProjectImages from "./pages/ProjectImages";
import ProjectVideos from "./pages/ProjectVideos";
import ProjectAccessCodes from "./pages/ProjectAccessCodes";
import Orders from "./pages/Orders";
import OrderDetails from "./pages/OrderDetails";
import GalleryLogin from "./pages/GalleryLogin";
import GalleryPackageSelection from "./pages/GalleryPackageSelection";
import Gallery from "./pages/Gallery";
import GalleryCart from "./pages/GalleryCart";
import GalleryCheckout from "./pages/GalleryCheckout";
import Analytics from "./pages/Analytics";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Profile from "./pages/Profile";
import Downloads from "./pages/Downloads";
import PurchasedGallery from "./pages/PurchasedGallery";
import PayPalReturn from "./pages/PayPalReturn";
import AdminUsers from "./pages/AdminUsers";
import LegalPage from "./pages/LegalPage";
import Setup from "./pages/Setup";
import Download from "./pages/Download";
import ShippingOverview from "./pages/ShippingOverview";
import SystemLogs from "./pages/SystemLogs";
import ProtectedRoute from "./components/ProtectedRoute";

function Router() {
  return (
    <Switch>
      {/* Public routes */}
      <Route path="/setup" component={Setup} />
      <Route path="/download/:token" component={Download} />
      <Route path="/login" component={Login} />
      <Route path="/register" component={Register} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route path="/reset-password" component={ResetPassword} />
      <Route path="/gallery/login" component={GalleryLogin} />
      <Route path="/gallery/packages" component={GalleryPackageSelection} />
      <Route path="/gallery" component={Gallery} />
      <Route path="/gallery/cart" component={GalleryCart} />
      <Route path="/gallery/checkout" component={GalleryCheckout} />
      <Route path="/paypal/return" component={PayPalReturn} />
      <Route path="/purchased/:token" component={PurchasedGallery} />
      <Route path="/impressum" component={LegalPage} />
      <Route path="/privacy" component={LegalPage} />
      <Route path="/terms" component={LegalPage} />
      <Route path="/contact" component={LegalPage} />
      <Route path="/shipping" component={LegalPage} />
      <Route path="/payment" component={LegalPage} />
      {/* Protected routes (require authentication) */}
      <Route path={"/"}>{() => <ProtectedRoute component={Dashboard} />}</Route>
      <Route path="/dashboard">{() => <ProtectedRoute component={Dashboard} />}</Route>
      <Route path="/projects">{() => <ProtectedRoute component={Projects} />}</Route>
      <Route path="/projects/:id/images">{() => <ProtectedRoute component={ProjectImages} />}</Route>
      <Route path="/projects/:id/videos">{() => <ProtectedRoute component={ProjectVideos} />}</Route>
      <Route path="/projects/:id/codes">{() => <ProtectedRoute component={ProjectAccessCodes} />}</Route>
      <Route path="/orders">{() => <ProtectedRoute component={Orders} />}</Route>
      <Route path="/orders/:id/shipping">{() => <ProtectedRoute component={ShippingOverview} />}</Route>
      <Route path="/orders/:id">{() => <ProtectedRoute component={OrderDetails} />}</Route>
      <Route path="/settings">{() => <ProtectedRoute component={Settings} />}</Route>
      <Route path="/analytics">{() => <ProtectedRoute component={Analytics} />}</Route>
      <Route path="/profile">{() => <ProtectedRoute component={Profile} />}</Route>
      <Route path="/downloads">{() => <ProtectedRoute component={Downloads} />}</Route>
      {/* Admin-only routes */}
      <Route path="/admin/users">{() => <ProtectedRoute component={AdminUsers} adminOnly />}</Route>
      <Route path="/admin/logs">{() => <ProtectedRoute component={SystemLogs} adminOnly />}</Route>
      <Route path={"/404"} component={NotFound} />
      {/* Final fallback route */}
      <Route component={NotFound} />
    </Switch>
  );
}

// NOTE: About Theme
// - First choose a default theme according to your design style (dark or light bg), than change color palette in index.css
//   to keep consistent foreground/background color across components
// - If you want to make theme switchable, pass `switchable` ThemeProvider and use `useTheme` hook

function App() {
  const { data: settings } = trpc.settings.getPublicSettings.useQuery(undefined, { staleTime: 60_000 });
  const { data: iconData } = trpc.settings.getSystemIcon.useQuery(undefined, { staleTime: 300_000 });

  useEffect(() => {
    if (settings) {
      const systemName = settings.systemName || "Okulus";
      document.title = systemName;
    }
  }, [settings]);

  useEffect(() => {
    if (iconData?.url) {
      let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = iconData.url;
    }
  }, [iconData]);

  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light" switchable>
        <I18nProvider>
          <TooltipProvider>
            <Toaster />
            <Router />
          </TooltipProvider>
        </I18nProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
